import { EstateRelatedDto } from "@1games-ru/region-shared/dist/types/estate/estate";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import EstateMpProvider from "@/core/data-provider/data-mp/estate/estate-mp-provider";
import GameMpProvider from "@/core/data-provider/data-mp/game/game-mp-provider";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { clearOffer } from "@/domains/offer/slice";
import toast from "@/domains/toasts/helpers/toast";

export default function useOfferCreatorEstate(isShow: boolean) {
  const { t, i18n } = useTranslation("offer");
  const dispatch = useAppDispatch();

  const [estates, setEstates] = useState<EstateRelatedDto[]>([]);
  const [ownerEstates, setOwnerEstates] = useState<EstateRelatedDto[]>([]);
  const [pending, setPending] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await EstateMpProvider.getRelated();

      if (result instanceof RpcMpProviderFailedResponse) {
        console.error(result.error?.message ?? result);
        return;
      }

      setEstates(result.data.items);
      setOwnerEstates(result.data.items.filter((item) => item.isOwner));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const close = useCallback(() => {
    dispatch(clearOffer());
    GameMpProvider.closeCurrentInterface(true);
  }, [dispatch]);

  const create = useCallback(
    async (characterId: number, estateId: number, estatePrice: number, amount: number) => {
      setPending(true);
      try {
        if (estatePrice > amount) {
          toast.error(t("offer.errors.CREATE_TRADE_OFFER_MIN_PRICE"));
          return false;
        }

        const result = await EstateMpProvider.createTrade(estateId, characterId, amount);

        if (result instanceof RpcMpProviderFailedResponse) {
          const code = result.error?.message;
          if (code && i18n.exists(`offer:offer.errors.${code}`)) {
            console.error(code);
            toast.error(t(`offer.errors.${code}`));
          } else {
            console.error(code ?? result);
            toast.error(t("offer.errors.createError"));
          }
          close();
          return false;
        }

        toast.success(t("offer.toast.accepted"));
        close();
        return true;
      } catch (e) {
        console.error(e);
        toast.error(t("offer.errors.createError"));
        return false;
      } finally {
        setPending(false);
      }
    },
    [i18n, t, close],
  );

  useEffect(() => {
    if (isShow) {
      load();
    } else {
      setEstates([]);
      setOwnerEstates([]);
    }
  }, [isShow, load]);

  return {
    estates,
    ownerEstates,
    create,
    close,
    pending,
    loading,
  };
}
